import {
    Ref,
    computed,
    ref,
    shallowRef,
} from 'vue';
import type { Component, DefineComponent, VueConstructor } from 'vue';
import type { PropOptions, PropType } from 'vue';

type InferPropType<T> =
    T extends PropOptions<infer V>
        ? unknown extends V ? T : V
        : T extends PropType<infer V>
            ? V
            : T;

type ExtractPropsFromDefinition<T> = {
    [K in keyof T]?: InferPropType<T[K]>
};

export type ExtractModalResult<C> =
    C extends DefineComponent<any, any, any, any, any, any, any, infer E>
        ? E extends { close: (result: infer A) => any }
            ? A
            : 'no close'
        : 'no emits';

type ExtractComponentProps<C> =
    C extends { props: infer P }
        ? ExtractPropsFromDefinition<P>
        : C extends VueConstructor<infer V>
            ? V extends { $props: infer P }
                ? Omit<P, 'key' | 'ref' | 'class' | 'style' | 'slot'>
                : Record<string, unknown>
            : Record<string, unknown>;

export interface ModalOptions<
    C extends Component,
    RETURN extends ExtractModalResult<C>,
> {
    /**
     * Dynamic import function for the modal component.
     * The component is expected to emit a 'close' event, and optionally a 'cancel' event.
     */
    component: () => Promise<{ default: C }>;
    /**
     * Props to pass to the modal component.
     * Type is inferred from the component when possible.
     */
    props?: Partial<ExtractComponentProps<C>>;
    /**
     * If true, clicking background or pressing escape won't close the modal.
     * @default false
     */
    persistent?: boolean;
    /**
     * Default value to resolve if the modal is closed without an explicit result.
     */
    defaultReturn?: RETURN;
}

export interface ActiveModal<RETURN = unknown> {
    component: (() => Promise<{ default: Component }>) | null;
    props: Record<string, unknown>;
    persistent: boolean;
    resolve: (value: RETURN | null) => void;
}

export interface ModalController {
    openModal: <C extends Component, RETURN extends ExtractModalResult<C>>(
        options: ModalOptions<C, RETURN>
    ) => Promise<RETURN | null>;
    closeModal: (result?: unknown) => void;
}

// Module-level singleton — no provide/inject needed; any component can call openModal/closeModal directly
const _activeModal: Ref<ActiveModal | null> = shallowRef(null);
const _defaultReturnValue = ref<unknown>(null);

export function openModal<C extends Component, RETURN extends ExtractModalResult<C>> (options: ModalOptions<C, RETURN>): Promise<RETURN | null> {
    _defaultReturnValue.value = options.defaultReturn ?? null;
    return new Promise<RETURN | null>(resolve => {
        _activeModal.value = {
            component: options.component,
            props: (options.props || {}),
            persistent: options.persistent ?? false,
            resolve,
        } as ActiveModal;
    });
}

export function closeModal (result?: unknown) {
    if (_activeModal.value) {
        _activeModal.value.resolve(result ?? _defaultReturnValue.value ?? null);
        _activeModal.value = null;
    }
}

/**
 * For the top-level view (App.vue) to render the active modal.
 */
export function useModalRenderer () {
    return {
        activeModal: _activeModal,
        modalOpen: computed(() => _activeModal.value !== null),
        closeModal,
    };
}

/**
 * Returns functions to programmatically open and close the global modal.
 * Can be called from any component — no ancestor provider required.
 */
export function useModal (): ModalController {
    return {
        openModal,
        closeModal,
    };
}
